import { useCallback, useEffect, useState } from 'react';
import { getAvatarUrl } from 'utils/media';
import { APP_DOMAIN } from 'utils/urls';

/**
 * The avatar pool manifest, `meta/avatar.json` (EN-11264): `avatars` is the
 * default pool (file names), and `status` holds one array of file names per
 * status set key, in unlock order:
 *
 *   { "avatars": ["001-saloon.png", ...],
 *     "status": { "cowboy": ["cowboy-1-bandana.png", ...], ... } }
 *
 * The file name is the avatar id the profile stores. Until the manifest has a
 * `status` key every set reads empty and the copy names no count.
 *
 * Fetched once per page and shared; Change Avatar asks for a fresh copy each
 * time it opens, so art added to the pool shows without a deploy. A failed
 * fetch leaves the lists empty.
 */
export interface AvatarManifest {
  avatars: string[];
  status: Record<string, string[]>;
}

const EMPTY: AvatarManifest = { avatars: [], status: {} };

const fileList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((f): f is string => typeof f === 'string' && !!f) : [];

export function parseAvatarManifest(json: unknown): AvatarManifest {
  const raw = (json ?? {}) as { avatars?: unknown; status?: unknown };
  const status: Record<string, string[]> = {};
  if (raw.status && typeof raw.status === 'object' && !Array.isArray(raw.status)) {
    Object.entries(raw.status as Record<string, unknown>).forEach(([key, files]) => {
      const list = fileList(files);
      if (list.length) status[key] = list;
    });
  }
  return { avatars: fileList(raw.avatars), status };
}

let cache: AvatarManifest | null = null;
let pending: Promise<AvatarManifest> | null = null;
const listeners = new Set<(m: AvatarManifest) => void>();

/** The shared manifest; `fresh` refetches and updates every reader. */
export function loadAvatarManifest({ fresh = false } = {}): Promise<AvatarManifest> {
  if (cache && !fresh) return Promise.resolve(cache);
  if (!pending) {
    pending = fetch(`https://media.${APP_DOMAIN}/meta/avatar.json`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => parseAvatarManifest(json))
      .catch(() => cache ?? EMPTY)
      .then((manifest) => {
        cache = manifest;
        pending = null;
        listeners.forEach((l) => l(manifest));
        return manifest;
      });
  }
  return pending;
}

/** Tests only. */
export function resetAvatarManifest() {
  cache = null;
  pending = null;
}

/** The manifest, loaded once when `active`, and kept current by later loads. */
export function useAvatarManifest(active = true): AvatarManifest {
  const [manifest, setManifest] = useState<AvatarManifest>(() => cache || EMPTY);
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    const update = (m: AvatarManifest) => {
      if (alive) setManifest(m);
    };
    listeners.add(update);
    loadAvatarManifest().then(update);
    return () => {
      alive = false;
      listeners.delete(update);
    };
  }, [active]);
  return manifest;
}

/**
 * Image URLs of a status set, for counts and the banner stub. Pass `active`
 * false (ops.streaks off) to skip the fetch entirely.
 */
export function useStatusAvatars(active = true): (setKey: string | undefined) => string[] {
  const { status } = useAvatarManifest(active);
  return useCallback(
    (setKey) => (setKey && status[setKey] ? status[setKey].map(getAvatarUrl) : []),
    [status]
  );
}
